import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignPreviewResponse, EmailBodyDto } from '@carbonwebmail/contracts'
import { isEmptyBody } from '@carbonwebmail/domain'
import { useEffect, useState } from 'react'

/**
 * What a subject and a body would look like sent.
 *
 * Rendered by the server, through the same two functions that build and fill a
 * campaign at create and at save. That is why it is a request rather than a
 * render: a preview drawn by a second renderer can disagree with the message,
 * and the browser holds a cached bundle that may be older than the server, so
 * "the same code" would not stay true for long.
 *
 * Asked for after the typing stops, like the segment builder's reach. Each one
 * renders a whole email, and firing one per keystroke would put a request per
 * character on a shared server for an answer nobody reads until they pause.
 *
 * Shared by the composer and the template editor, which want the identical
 * thing. A second copy of a debounce is a second place for the stale-answer
 * guard to be forgotten.
 */

const DEBOUNCE_MS = 400

export function usePreview({
  client,
  subject,
  body,
}: {
  readonly client: ApiClient
  readonly subject: string
  readonly body: EmailBodyDto
}): CampaignPreviewResponse | undefined {
  const [preview, setPreview] = useState<CampaignPreviewResponse | undefined>(undefined)

  /**
   * What the body *says*, not which object it is.
   *
   * A caller that builds its body inline — `designedBody(html, '')` — hands over
   * a new object on every render, and an effect keyed on that re-runs when
   * nothing has changed. Its own answer arriving is a render, so it asked again,
   * and the composer's preview sat there requesting a whole rendered email every
   * four hundred milliseconds for as long as it was open.
   *
   * The effect reads the body back out of this rather than closing over the
   * prop, so what it depends on and what it uses are the same thing — which is
   * also what lets the dependency list stay honest.
   */
  const contents = JSON.stringify(body)

  useEffect(() => {
    const asked = JSON.parse(contents) as EmailBodyDto
    if (subject.trim() === '' || isEmptyBody(asked)) {
      // Nothing to render. Keeping the last preview would show a message for
      // text that is no longer on screen.
      setPreview(undefined)
      return
    }

    let current = true
    const timer = setTimeout(() => {
      void client
        .campaignPreview({ subject, body: asked })
        .then((rendered) => {
          if (current) setPreview(rendered)
        })
        .catch(() => {
          // The preview is an aid, not the screen. A failed one shows nothing
          // rather than a stale message somebody might believe.
          if (current) setPreview(undefined)
        })
    }, DEBOUNCE_MS)

    // Cancels the timer and marks the answer stale, so a slow response for
    // older text cannot land after a newer one.
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [client, subject, contents])

  return preview
}
